import styled from "styled-components";

export interface ConveyanceSummary {
  id: string;
  name: string;
  totalAmount: number;
  totalApproved?: number;
  totalCount?: number;
}

interface Props {
  data: ConveyanceSummary[];
  isShowTotalConveyance?: boolean;
  isShowApprovedAmount?: boolean;
}

const SummaryShell = styled.div`
  display: grid;
  gap: 8px;
`;

const SummaryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 9px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
`;

const SummaryItem = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
`;

const SummaryLabel = styled.span`
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const SummaryValue = styled.span`
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
`;

const DetailsList = styled.div`
  display: grid;
  gap: 7px;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 2px;
`;

const DetailsCard = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 9px 10px;
  border: 1px solid #edf2f7;
  border-radius: 8px;
  background: #ffffff;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
    gap: 7px;
  }
`;

const MemberBlock = styled.div`
  min-width: 0;
`;

const MemberName = styled.p`
  margin: 0;
  color: #0f172a;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MemberId = styled.p`
  margin: 3px 0 0 0;
  color: #64748b;
  font-size: 10px;
  line-height: 1.2;
`;

const DetailValues = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;

  @media (max-width: 575px) {
    justify-content: flex-start;
  }
`;

const DetailPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
  padding: 4px 7px;
  border-radius: 999px;
  color: #334155;
  background: #f8fafc;
  font-size: 11px;
  line-height: 1;
`;

const PillLabel = styled.span`
  color: #64748b;
  font-weight: 500;
`;

const PillValue = styled.span`
  color: #0f172a;
  font-weight: 600;
`;

const formatAmount = (value?: number) =>
  Number(value ?? 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

const ConveyanceSummaryList = ({
  data,
  isShowTotalConveyance = true,
  isShowApprovedAmount = true,
}: Props) => {
  if (!data?.length) return <></>;

  const totalAmount = data.reduce(
    (total, item) => total + Number(item.totalAmount ?? 0),
    0
  );
  const totalApproved = data.reduce(
    (total, item) => total + Number(item.totalApproved ?? 0),
    0
  );
  const totalConveyance = data.reduce(
    (total, item) => total + Number(item.totalCount ?? 0),
    0
  );

  return (
    <SummaryShell>
      <SummaryBar>
        <SummaryItem>
          <SummaryLabel>Members</SummaryLabel>
          <SummaryValue>{data.length}</SummaryValue>
        </SummaryItem>
        {isShowTotalConveyance ? (
          <SummaryItem>
            <SummaryLabel>Conveyance</SummaryLabel>
            <SummaryValue>{totalConveyance}</SummaryValue>
          </SummaryItem>
        ) : null}
        <SummaryItem>
          <SummaryLabel>Amount</SummaryLabel>
          <SummaryValue>{formatAmount(totalAmount)}</SummaryValue>
        </SummaryItem>
        {isShowApprovedAmount ? (
          <SummaryItem>
            <SummaryLabel>Approved</SummaryLabel>
            <SummaryValue>{formatAmount(totalApproved)}</SummaryValue>
          </SummaryItem>
        ) : null}
      </SummaryBar>

      <DetailsList>
        {data.map((item) => (
          <DetailsCard key={item.id}>
            <MemberBlock>
              <MemberName title={item.name}>{item.name || "NA"}</MemberName>
              <MemberId>{item.id}</MemberId>
            </MemberBlock>
            <DetailValues>
              {isShowTotalConveyance ? (
                <DetailPill>
                  <PillLabel>No.</PillLabel>
                  <PillValue>{item.totalCount ?? 0}</PillValue>
                </DetailPill>
              ) : null}
              <DetailPill>
                <PillLabel>Amount</PillLabel>
                <PillValue>{formatAmount(item.totalAmount)}</PillValue>
              </DetailPill>
              {isShowApprovedAmount ? (
                <DetailPill>
                  <PillLabel>Approved</PillLabel>
                  <PillValue>{formatAmount(item.totalApproved)}</PillValue>
                </DetailPill>
              ) : null}
            </DetailValues>
          </DetailsCard>
        ))}
      </DetailsList>
    </SummaryShell>
  );
};

export default ConveyanceSummaryList;
